import { DocumentNode } from 'graphql'
import { print } from 'graphql/language/printer'
import get from 'lodash/get'

import { getValidToken, requireSessionToken, TokenRequest } from '@avantstay/auth-token'

import { backofficeRouter } from '@arriere/backofficeRouter'
import { showSessionExpiredAlert } from '@contexts/Auth/sessionExpiredAlert'
import { trackError } from '@tracking/customEventReport'
import { IS_PRODUCTION } from '@utils/constants'
import history from '@utils/history'

const isUserNotAuthorized = ({ message }: { message: string }) => {
  if (message === 'User not authorized') {
    history.push('/error?errorType=not_authorized')
  }
}

let userEmail = ''
const setUserEmail = (email: string) => {
  userEmail = email
}

const fetchApi = async <T = any>(
  query: DocumentNode,
  variables?: { [key: string]: any },
  signal?: AbortSignal,
  apiUrl?: string,
): Promise<any> => postGraphQL<T>({ query, variables, signal, apiUrl, requiresAuth: true })

const fetchPublicApi = (
  query: DocumentNode,
  variables?: { [key: string]: any },
  signal?: AbortSignal,
) =>
  postGraphQL({
    query,
    variables,
    signal,
    apiUrl: process.env.REACT_APP_ARRIERE_PUBLIC as string,
    requiresAuth: false,
  })

interface GraphQLPost {
  query: DocumentNode
  variables?: { [key: string]: any }
  signal?: AbortSignal
  apiUrl?: string
  /** False for the public endpoint, which also serves anonymous requests. */
  requiresAuth: boolean
}

const postGraphQL = async <T = any>({
  query,
  variables,
  signal,
  apiUrl,
  requiresAuth,
}: GraphQLPost): Promise<any> => {
  const post = async (tokenRequest?: TokenRequest) =>
    fetch(apiUrl || backofficeRouter.url || (process.env.REACT_APP_ARRIERE_BACKOFFICE as string), {
      method: 'POST',
      headers: await buildHeaders(requiresAuth, tokenRequest),
      body: JSON.stringify({
        query: print(query),
        variables,
      }),
      cache: 'default',
      signal,
    })
  // A 401 can mean the token expired in flight; retry once with a newly minted one.
  const firstResponse = await post()
  const response = firstResponse.status === 401 ? await post({ skipCache: true }) : firstResponse
  const data = await parseGraphQLBody(response)
  const unauthorizedToPerform = response.status === 401
  const errors = get(data, 'errors', [])
  const hasErrors = Boolean(errors.length)

  if (unauthorizedToPerform) showSessionExpiredAlert()

  if (hasErrors) {
    if (IS_PRODUCTION) {
      errors.forEach((error: any) => {
        trackError(`API error: ${JSON.stringify(error)}`, error.message)
      })
    }
    isUserNotAuthorized(errors[0])
  }

  return data as { data: T } | { errors: Array<string> }
}

/**
 * A load balancer, firewall or router timeout can answer with a non-JSON body (an HTML 502/504
 * page, for example). The request still rejects, but with the HTTP status instead of a JSON
 * syntax error, and it is reported like a GraphQL error.
 */
const parseGraphQLBody = async (response: Response) => {
  try {
    return await response.json()
  } catch {
    const message = `GraphQL request failed with HTTP ${response.status} and a non-JSON body`
    if (IS_PRODUCTION) trackError(`API error: ${message}`, { status: response.status })
    throw new Error(message)
  }
}

/** Without a session token an authenticated request is not sent; a public one goes out anonymously. */
const buildHeaders = async (
  requiresAuth: boolean,
  tokenRequest?: TokenRequest,
): Promise<Record<string, string>> => {
  const token = requiresAuth
    ? await requireSessionToken(tokenRequest)
    : await getValidToken(tokenRequest)

  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (userEmail) headers['x-teammate'] = userEmail
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

type FetchOptions = {
  url?: string
  headers?: { [key: string]: string }
  body?: { [key: string]: any }
  signal?: AbortSignal
}

const fetchTemplates = async <T = any>(options: FetchOptions): Promise<T> => {
  const emailURL = process.env.REACT_APP_MODELE_EMAIL as string

  const response = await fetch(`${emailURL}/${options.url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options.body),
  })

  if (!response.ok) throw new Error(`Fetch failed with status: ${response.status}`)

  return response.json()
}

// `REACT_APP_MODELE_EMAIL` points at the modele service's `/email` route base.
// PDF templates live at a sibling route (`/pdf/<Name>`) on the same host, so we
// resolve the host from that env var and append the requested template path.
const fetchTemplatePdf = async (options: FetchOptions): Promise<Blob> => {
  const modeleHost = (process.env.REACT_APP_MODELE_EMAIL as string).replace(/\/email\/?$/, '')

  const response = await fetch(`${modeleHost}/${options.url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(options.body),
  })

  if (!response.ok) throw new Error(`Fetch failed with status: ${response.status}`)

  return response.blob()
}

export { fetchPublicApi, fetchTemplatePdf, fetchTemplates, setUserEmail }
export default fetchApi
